import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { UploadDialog } from "../components/UploadDialog";
import { AISearchDialog } from "../components/AISearch";
import { CaptionsEditor } from "../components/CaptionsEditor";
import { ScratchPicker } from "../components/ScratchPicker";
import { api, API, formatBytes } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ArrowLeft, Heart, Share2, Trash2, Send, Eye, FileText, Captions, Sticker, Tag, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function MediaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, isPremium } = useAuth();
  const [media, setMedia] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [captionsOpen, setCaptionsOpen] = useState(false);
  const [scratchOpen, setScratchOpen] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([
        api.get(`/media/${id}`),
        api.get(`/media/${id}/comments`),
      ]);
      setMedia(m.data); setComments(c.data || []);
    } catch { toast.error("Failed to load media"); nav("/library"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const res = await api.post(`/media/${id}/comments`, { text: text.trim() });
      setComments((cs) => [...cs, res.data]); setText("");
    } catch { toast.error("Failed to comment"); }
  };

  const like = async () => {
    try { const res = await api.post(`/media/${id}/like`); setMedia((m) => ({ ...m, likes: res.data.likes })); }
    catch {}
  };

  const remove = async () => {
    if (!window.confirm("Delete this media? It will be hidden from the squad.")) return;
    try { await api.delete(`/media/${id}`); toast.success("Removed"); nav("/library"); }
    catch { toast.error("Delete failed"); }
  };

  if (!media) return <div className="min-h-screen bg-black text-white" />;

  const isVideo = media.media_kind === "video";
  const isImage = media.media_kind === "image";
  const isAudio = media.media_kind === "audio";
  const captionsUrl = media.captions_vtt ? `${API}/media/${id}/captions.vtt` : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="media-detail-page">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onUploadOpen={() => setUploadOpen(true)} dark />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        <Link to="/library" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6" data-testid="back-to-library">
          <ArrowLeft className="w-4 h-4" /> Library
        </Link>

        <div className="grid lg:grid-cols-[1fr_380px] gap-10">
          <div>
            <div className="bg-black border border-white/10 aspect-video flex items-center justify-center overflow-hidden relative">
              {isVideo && (
                <video
                  key={`${id}-${captionsUrl ? "cap" : "nocap"}`}
                  src={`${API}/media/${id}/stream`}
                  controls
                  autoPlay
                  className="w-full h-full"
                  crossOrigin="anonymous"
                  data-testid="video-player"
                >
                  {captionsUrl && showCaptions && (
                    <track
                      kind="subtitles"
                      src={captionsUrl}
                      srcLang="en"
                      label="English"
                      default
                    />
                  )}
                </video>
              )}
              {isImage && (
                <img src={`${API}/media/${id}/stream`} alt={media.title} className="w-full h-full object-contain" data-testid="image-viewer" />
              )}
              {isAudio && (
                <div className="text-center">
                  <audio src={`${API}/media/${id}/stream`} controls autoPlay className="mb-4" data-testid="audio-player" />
                  <div className="text-white/60 text-sm">{media.original_filename}</div>
                  {media.captions_vtt && (
                    <div className="mt-4 max-w-md mx-auto p-4 border border-white/15 text-left text-sm text-white/80" data-testid="audio-captions-preview">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E60000] mb-2">Captions preview</div>
                      <pre className="whitespace-pre-wrap font-mono text-xs">{media.captions_vtt.slice(0, 600)}</pre>
                    </div>
                  )}
                </div>
              )}
              {!isVideo && !isImage && !isAudio && (
                <div className="text-center p-10">
                  <FileText className="w-12 h-12 text-white/40 mx-auto" strokeWidth={1.2} />
                  <div className="mt-4 font-heading font-black text-xl">{media.original_filename}</div>
                  <a href={`${API}/media/${id}/stream`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-[#E60000] underline">
                    Open file →
                  </a>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-start justify-between flex-wrap gap-4">
              <div className="min-w-0">
                <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">{media.title}</h1>
                {media.description && <p className="mt-3 text-white/70 max-w-2xl">{media.description}</p>}
                {media.tags?.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="auto-tags">
                    <Tag className="w-3.5 h-3.5 text-[#E60000]" />
                    {media.tags.map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-white/20 text-white/70">
                        {t}
                      </span>
                    ))}
                    <span className="text-[10px] uppercase tracking-wider text-white/40">· auto-generated</span>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-4 text-xs text-white/50 font-mono">
                  <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {media.views}</span>
                  <span>{formatBytes(media.size)}</span>
                  <span>{media.content_type}</span>
                  <span>{new Date(media.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {(isVideo || isAudio) && (
                  <Button
                    variant="outline"
                    onClick={() => setCaptionsOpen(true)}
                    className="rounded-sm bg-transparent border-white/20 text-white hover:bg-white hover:text-black"
                    data-testid="captions-button"
                  >
                    <Captions className="w-4 h-4 mr-2" /> Captions
                    {!isPremium && <Crown className="w-3 h-3 ml-2 text-[#E60000]" />}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setScratchOpen(true)}
                  className="rounded-sm bg-transparent border-white/20 text-white hover:bg-white hover:text-black"
                  data-testid="scratch-overlay-button"
                >
                  <Sticker className="w-4 h-4 mr-2" /> Scratch FX
                </Button>
                <Button variant="outline" onClick={like} className="rounded-sm bg-transparent border-white/20 text-white hover:bg-white hover:text-black" data-testid="like-button">
                  <Heart className="w-4 h-4 mr-2" /> {media.likes || 0}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }}
                  className="rounded-sm bg-transparent border-white/20 text-white hover:bg-white hover:text-black"
                  data-testid="share-button"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                {user?.user_id === media.user_id && (
                  <Button variant="outline" onClick={remove} className="rounded-sm bg-transparent border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" data-testid="delete-button">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                )}
              </div>
            </div>

            {captionsUrl && (isVideo) && (
              <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
                <Captions className="w-3.5 h-3.5 text-[#E60000]" />
                Captions on
                <button
                  onClick={() => setShowCaptions((s) => !s)}
                  data-testid="toggle-captions"
                  className="ml-2 underline hover:text-white"
                >
                  {showCaptions ? "Hide" : "Show"}
                </button>
              </div>
            )}
          </div>

          {/* Comments */}
          <aside className="border-l border-white/10 pl-8" data-testid="comments-panel">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000]">Squad notes</div>
            <h2 className="font-heading font-black text-2xl mt-1">Comments · {comments.length}</h2>

            <div className="mt-5 space-y-4 max-h-[60vh] overflow-auto pr-2">
              {comments.length === 0 && <div className="text-white/40 text-sm">Be the first to drop a note.</div>}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                  <Avatar className="w-8 h-8 rounded-sm">
                    <AvatarImage src={c.user_picture} />
                    <AvatarFallback className="rounded-sm bg-[#E60000] text-white text-xs">{c.user_name?.[0] || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold tracking-tight">{c.user_name}</div>
                    <div className="text-sm text-white/75">{c.text}</div>
                    <div className="text-[10px] text-white/30 mt-0.5 font-mono">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Drop a frame-perfect note…"
                className="rounded-sm bg-white/5 border-white/15 text-white placeholder:text-white/35"
                data-testid="comment-input"
              />
              <Button onClick={send} className="rounded-sm bg-[#E60000] hover:bg-[#CC0000]" data-testid="comment-submit">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </aside>
        </div>
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <AISearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <CaptionsEditor open={captionsOpen} onOpenChange={setCaptionsOpen} mediaId={id} onSaved={load} />
      <ScratchPicker open={scratchOpen} onOpenChange={setScratchOpen} onPickAsset={(a) => toast.success(`${a.name} ready · drop into your edit`)} />
    </div>
  );
}
